import { memo, useEffect, useMemo } from 'react';
import { APP_CONFIG, createCache } from '@sd/shared';
import { Alert } from 'react-bootstrap';
const isHTMLElement = (elm: any): elm is HTMLElement => elm.tagName;

const CACHE = createCache('badLinkDetection', { defaultTtl: 1000, storageType: 'sessionStorage' });
type ElementInfo = {
  innerText: string;
  outerHTML: string;
};

const ENABLED = ['test', 'dev'].includes(APP_CONFIG.build.environment);

/**
 * Automatically detect when links on the page that should direct to internal routes are rendered
 * in a way that does not cause the full page to be reloaded.
 *
 * Note: Only enabled in the DEV environment.
 */
export const DetectAccidentalFullPageRefreshes = memo(() => {
  const lastClickedThing = useMemo(() => CACHE.get<ElementInfo>('lastClickedThing'), []);
  const lastWindowUnload = useMemo(() => CACHE.get<ElementInfo>('lastWindowUnload'), []);
  useEffect(() => {
    if (!ENABLED) return;

    const handleClick = (e: MouseEvent) => {
      if (isHTMLElement(e.target) && e.target.getAttribute('data-allow-full-page-refresh') !== 'true') {
        CACHE.set<ElementInfo>('lastClickedThing', { innerText: e.target.innerText, outerHTML: e.target.outerHTML });
      } else {
        CACHE.delete('lastClickedThing');
      }
    };
    window.addEventListener('click', handleClick);

    const handleBeforeUnload = () => {
      CACHE.set<number>('lastWindowUnload', new Date().valueOf());
    };
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('click', handleClick);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  if (ENABLED) {
    const referrerBaseUrl = /^https?:\/\/(.*?\/)/.exec(document.referrer)?.[0];
    if (lastWindowUnload && referrerBaseUrl && window.location.href.startsWith(referrerBaseUrl)) {
      return (
        <Alert variant="warning" data-testid="full-page-refresh-message">
          <Alert.Heading>Improper Page Refresh</Alert.Heading>
          <p>Developer Message</p>
          The last page you were on was directed to an internal route but triggered a full page refresh. This suggests
          that one of the following things occurred:
          <ul>
            <li>
              A link was rendered as an <code>&lt;a&gt;</code> instead of a <code>&lt;Link&gt;</code>.
            </li>
            <li>
              A navigation was triggered with window.location assignment instead of the <code>useHistory</code> hook.
            </li>
          </ul>
          Please update the code to avoid this full page refresh
          <hr />
          Last page: {document.referrer}
          {lastClickedThing && (
            <>
              <br />
              Last thing that was clicked before the page refreshed:
              <br />
              <pre>{JSON.stringify(lastClickedThing, null, 2)}</pre>
            </>
          )}
        </Alert>
      );
    }
  }
  return null;
});
